"use client";

import * as X from "@/ui";
import { NumericFormat } from "react-number-format";

import {
  getVatAmount,
  getRefundableAmount,
  findRefundableProportionOfVat,
  CountryVat,
  type Country,
} from "./logic";

import "./styles/RangeSlider.scss";


export { CountryVat, type Country };


const PriceInputMin = 100;
const PriceInputMax = 100_000;
const PriceInputInitial = 3_600;

const SliderBullets = [100, 150, 300, 500, 1_000, 2_000, 5_000, 20_000, 100_000];
const SliderStepsPerBullet = 4;

const VatFractionalDigits = 0;
const RefundFractionalDigits = 0;


export function useCalculator() {
  const [country, setCountry] = X.useState<Country>("FR");
  const [grossAmount, setGrossAmount] = X.useState<number>(PriceInputInitial);
  const [validValue, setValidValue] = X.useState<boolean>(true);

  const { createFormatter } = useNumberFormat();

  const formatVat = createFormatter({
    minimumFractionDigits: VatFractionalDigits,
    maximumFractionDigits: VatFractionalDigits,
  });

  const formatAmount = createFormatter({
    minimumFractionDigits: RefundFractionalDigits,
    maximumFractionDigits: RefundFractionalDigits,
  });

  return {
    country, setCountry,
    grossAmount, setGrossAmount,
    setValidValue,

    sliderValue: validValue ? grossAmount : 0,
    vatAmount: validValue ? formatVat(getVatAmount(country, grossAmount)) : "-",
    refundableAmount: validValue ? formatAmount(getRefundableAmount(country, grossAmount)) : "-",
    refundablePercentOfVat: validValue ? findRefundableProportionOfVat(grossAmount) * 100 : "-",
  };
}


export function NonLinearSlider(props: {
  value: number;
  onChange: (value: number) => void;
}) {
  const steps = X.useMemo(() => {
    const steps = [] as Array<{ id: number; amount: number }>;

    for (let bulletId = 0; bulletId < SliderBullets.length; bulletId++) {
      const bulletAmount = SliderBullets[bulletId]!;
      const bulletStepId = bulletId * SliderStepsPerBullet;
      steps.push({ id: bulletStepId, amount: bulletAmount });

      const nextBulletId = bulletId + 1;
      const nextBulletAmount = SliderBullets[nextBulletId];

      if (typeof nextBulletAmount === "number") {
        const amountPerStep = (nextBulletAmount - bulletAmount) / SliderStepsPerBullet;

        for (let j = 1; j < SliderStepsPerBullet; j++) {
          const stepId = bulletStepId + j;
          const stepAmount = bulletAmount + (j * amountPerStep);
          steps.push({ id: stepId, amount: stepAmount })
        }
      }
    }

    const findId = (amount: number) => {
      const step = steps.findLast(step => amount >= step.amount);
      return step?.id ?? 0;
    };

    const toAmount = (stepId: number) => {
      return steps.find(step => step.id === stepId)?.amount!;
    };

    return {
      findId,
      toAmount,
      length: steps.length,
    };

  }, [SliderBullets, SliderStepsPerBullet]);

  const step = steps.findId(props.value);
  const progressBarWidth = (100 * (step / steps.length)).toFixed(2);

  return (
    <>
      {/* background yellow track + bullets */}
      <div className="w-full relative h-[40px]">
        {/* the transluent "disabled", full-width yellow track */}
        <div className="absolute inset-x-[2px] xl:inset-x-[5px] top-[2px] xl:top-[2px] h-[4px] xl:h-[6px] rounded-full bg-yellow/30" />

        {/* the opaque, range-width yellow track */}
        <div className="absolute left-[2px] xl:left-[5px] top-[2px] xl:top-[2px] h-[4px] xl:h-[6px] rounded-full bg-yellow" style={{ width: `${progressBarWidth}%` }} />

        {/* the bullets */}
        <div className="absolute inset-x-[2px] xl:inset-x-[3px] top-[2px] xl:top-[4px] h-[4px] xl:h-[6px] flex justify-between">
          {Array.from({ length: steps.length }).map((_, i) => (
            <div key={i} className="relative flex flex-col items-center flex-1">
              <div className="absolute xl:-top-[2px] left-1/2 -translate-x-1/2 h-[4px] xl:h-[6px]">
                {X.call(() => {
                  if (i % 4 === 0) {
                    return (
                      <div className={X.clsx(
                        "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[8px] w-[8px] xl:h-[10px] xl:w-[10px] rounded-full border",
                        step >= i ? "bg-yellow border-black" : "bg-white/30 border-black/30",
                      )} />
                    );
                  }
                })}
              </div>
            </div>
          ))}
        </div>

        {/* the control range slider (invisible track + large bullet) */}
        <input
          name="amount-slider"
          type="range"
          min={0}
          max={steps.length - 1}
          step={1}
          value={step}
          className="w-full absolute inset-x-0 top-0"
          onChange={ev => {
            navigator.vibrate?.(5);
            const stepId = Number(ev.target.value);
            const amount = steps.toAmount(stepId);
            props.onChange(amount);
          }}
        />

        {/* the price amount labels */}
        <div className="absolute inset-x-[2px] xl:inset-x-[3px] top-[12px] xl:top-[16px] flex justify-between text-[12px] text-gray">
          {Array.from({ length: steps.length }).map((_, i) => (
            <div key={i} className="relative flex flex-col items-center flex-1 bg-red-300">
              <div className="absolute top-0 left-1/2 -translate-x-1/2">
                {X.call(() => {
                  if (i % 4 === 0) {
                    const amount = steps.toAmount(i);
                    return amount >= 1000 ? `${Math.round(amount / 1000)}k` : amount;
                  }
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}


export function PriceInput(props: X.PageProps<{
  value: number;
  onChange: (value: number) => void;
  onValidChange?: (valid: boolean) => void;
  className?: string;
}>) {
  const [displayValue, setDisplayValue] = X.useState<number>(props.value);
  const [isValid, setIsValid] = X.useState(true);

  const inControl = X.useStateRef<boolean>(false);

  const {
    formatNumber,
    decimalSeparator,
    thousandSeparator,
  } = X.getNumberFormat();

  const placeholder = useClientConstant(() => formatNumber(PriceInputInitial));

  X.useEffect([props.value], () => {
    // skip if own update
    if (inControl.current) {
      return;
    }

    const newValid = isAllowedValue(props.value);
    setDisplayValue(props.value);
    setIsValid(newValid);
    props.onValidChange?.(newValid);
  });

  const isAllowedValue = (value: number): boolean => {
    return Number.isFinite(value) && value >= PriceInputMin && value <= PriceInputMax;
  };

  const handleValueChange = ({ floatValue }: { floatValue?: number }) => {
    if (!inControl.current) return;
    if (floatValue === undefined) return;

    const isValid = isAllowedValue(floatValue);
    setDisplayValue(floatValue);
    setIsValid(isValid);

    if (!isValid) {
      const clampedValue = floatValue < PriceInputMin ? PriceInputMin : PriceInputMax;
      props.onChange(clampedValue);
      props.onValidChange?.(false);
      return;
    }

    props.onChange(floatValue);
    props.onValidChange?.(true);
  };

  return (
    <NumericFormat
      name="amount-input"
      onFocus={() => inControl.set(true)}
      onBlur={() => inControl.set(false)}
      value={displayValue}
      onValueChange={handleValueChange}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      decimalScale={2}
      placeholder={placeholder}
      className={X.clsx(
        props.className,
        !isValid && "text-red-500",
      )}
    />
  );
}


function useClientConstant<T>(factory: () => T): T | undefined {
  const [value, setValue] = X.useState<T | undefined>(undefined);

  X.useMountEffect(() => {
    setValue(factory());
  });

  return value;
}


export function useNumberFormat() {
  // ssg
  const [format, setFormat] = X.useState(() => X.getNumberFormat());

  // live
  X.useMountEffect(() => {
    setFormat(X.getNumberFormat());
  });

  return format;
}
